import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { brand } from "@/design-system/tokens";
import { roleForHost, loginRouteFor } from "@/features/hosting/hostRole";

/**
 * SplashScreen — first paint of the application.
 * Auto-forwards to /welcome (customer host) or the correct role login
 * (store/rider/admin subdomains) after a short brand reveal.
 * (Timing is purely presentational; no data is loaded here.)
 */
export default function SplashScreen() {
    const navigate = useNavigate();

    useEffect(() => {
        const role = roleForHost();
        const next = role === "customer" ? "/welcome" : loginRouteFor(role);
        const t = window.setTimeout(() => navigate(next, { replace: true }), 1200);
        return () => window.clearTimeout(t);
    }, [navigate]);

    return (
        <div
            data-testid="splash-screen"
            className="relative flex min-h-screen items-center justify-center overflow-hidden bg-brand text-white"
        >
            {/* Ambient blobs */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-white/25 blur-3xl" />
                <div className="absolute bottom-8 right-0 h-96 w-96 rounded-full bg-accentbrand/40 blur-3xl" />
            </div>

            <div className="relative z-10 flex flex-col items-center gap-6 px-6 text-center animate-fade-in">
                <div className="grid h-20 w-20 place-items-center rounded-3xl bg-white/15 backdrop-blur-md shadow-elevated">
                    <span className="text-4xl font-extrabold leading-none">T</span>
                </div>
                <h1
                    data-testid="splash-brand"
                    className="text-5xl font-extrabold tracking-tight sm:text-6xl"
                >
                    {brand.name}
                </h1>
                <p className="max-w-sm text-base text-white/85 sm:text-lg">
                    {brand.tagline}
                </p>
                <div className="mt-2 flex items-center gap-1.5" aria-hidden>
                    <span className="h-2 w-2 animate-pulse-brand rounded-full bg-white" />
                    <span className="h-2 w-2 animate-pulse-brand rounded-full bg-white [animation-delay:0.15s]" />
                    <span className="h-2 w-2 animate-pulse-brand rounded-full bg-white [animation-delay:0.3s]" />
                </div>
            </div>
        </div>
    );
}
